import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type { components, GeneticInfoDto } from '@/api/client';
import { mapGeneticVariant } from '@/features/patient-record/mappers/variant';
import { patientQueries } from '@/features/patient-record/queries';
import type { GeneticVariantViewModel } from '@/features/patient-record/types';

/**
 * Resource-level mutations for genetic variants. The dialog builds a complete
 * `GeneticInfoDTO` (see `variantForm.draftToDto`); these calls post it as-is:
 *
 *   - `create` — POST   `/persons/{personGuid}/genetic-info`
 *   - `update` — PUT    `/persons/{personGuid}/genetic-info/{id}` (full replace)
 *   - `remove` — DELETE `/persons/{personGuid}/genetic-info/{id}`
 *
 * PUT is a full replace, so the dialog reconstructs the whole DTO from the
 * original row plus the user's edits — no field is silently cleared.
 *
 * Optimistic rows are produced by running the outgoing DTO back through
 * `mapGeneticVariant`, so the cache mirrors exactly what a follow-up GET would
 * return instead of a hand-maintained parallel projection.
 */

type VariantDto = components['schemas']['Variant'];

export type VariantInput = GeneticInfoDto;

export interface UpdateVariantVariables {
  readonly id: string;
  readonly input: VariantInput;
}

interface MutationContext {
  readonly previous: GeneticVariantViewModel[] | undefined;
}

type CreateResult = UseMutationResult<
  GeneticVariantViewModel,
  Error,
  VariantInput,
  MutationContext
>;
type UpdateResult = UseMutationResult<
  GeneticVariantViewModel,
  Error,
  UpdateVariantVariables,
  MutationContext
>;
type RemoveResult = UseMutationResult<undefined, Error, string, MutationContext>;

/** Map an outgoing DTO (plus its id) to the optimistic view-model row. */
function optimisticRow(input: VariantInput, id: string): GeneticVariantViewModel {
  return mapGeneticVariant({ ...input, id } as unknown as VariantDto);
}

export function useVariantMutations(guid: string): {
  readonly create: CreateResult;
  readonly update: UpdateResult;
  readonly remove: RemoveResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.geneticVariants(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const create = useMutation<
    GeneticVariantViewModel,
    Error,
    VariantInput,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.variant_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.createVariant(guid, input);
      return mapGeneticVariant(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      const optimistic = optimisticRow(input, `optimistic-${Date.now().toString(36)}`);
      queryClient.setQueryData<GeneticVariantViewModel[]>(queryKey, [
        ...(ctx.previous ?? []),
        optimistic,
      ]);
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const update = useMutation<
    GeneticVariantViewModel,
    Error,
    UpdateVariantVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.variant_updated' },
    mutationFn: async ({ id, input }) => {
      const dto = await apiClient.updateVariant(guid, id, input);
      return mapGeneticVariant(dto);
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<GeneticVariantViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((v) => (v.id === id ? optimisticRow(input, id) : v)),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.variant_deleted' },
    mutationFn: (id) => apiClient.deleteVariant(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<GeneticVariantViewModel[]>(
        queryKey,
        (ctx.previous ?? []).filter((v) => v.id !== id),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { create, update, remove };
}
